/**
 * SeniorAvatar Component
 * 
 * Displays a senior's profile picture with fallback to initials.
 * 
 * Priority:
 * 1. photo_url (uploaded via R2)
 * 2. image_url (legacy/manual URL)
 * 3. Initials from name (first letter, uppercase)
 * 
 * Usage:
 * <SeniorAvatar senior={senior} size="md" />
 */

import React from 'react';

const SeniorAvatar = ({ 
  senior, 
  size = 'md',
  className = ''
}) => {
  // Determine which image URL to use (priority: photo_url > image_url)
  const imageUrl = senior.photo_url || senior.image_url;
  
  // Get initials from name (first letter, uppercase)
  const getInitials = (name?: string): string => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };
  
  const initials = getInitials(senior.name);
  
  // Size configurations
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-12 h-12 text-lg',
    lg: 'w-20 h-20 text-2xl'
  };
  
  const sizeClass = sizeClasses[size];
  
  // Base classes for all avatars
  const baseClasses = 'rounded-2xl flex items-center justify-center overflow-hidden';
  
  if (imageUrl) {
    // Show image if available
    return (
      <div className={`${baseClasses} ${sizeClass} ${className}`}>
        <img 
          src={imageUrl} 
          alt={senior.name || 'Senior'} 
          className="w-full h-full object-cover"
          onError={(e) => {
            // If image fails to load, hide it and show initials instead
            e.currentTarget.style.display = 'none';
            if (e.currentTarget.parentElement) {
              e.currentTarget.parentElement.innerHTML = `
                <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-400 to-blue-400 text-white font-semibold">
                  ${initials}
                </div>
              `;
            }
          }}
        />
      </div>
    );
  }
  
  // Fallback: Show initials
  return (
    <div 
      className={`
        ${baseClasses} 
        ${sizeClass} 
        bg-gradient-to-br from-purple-400 to-blue-400 
        text-white 
        font-semibold
        ${className}
      `}
    >
      {initials}
    </div>
  );
};

export default SeniorAvatar;
